/**
 * Resubmit Request Modal
 * Allows MEMBER to edit and resubmit REJECTED change requests
 *
 * Features:
 * - Shows admin feedback/notes
 * - Pre-fills form with previous data
 * - Supports both CREATE and UPDATE requests
 */

import React, { useState, useEffect } from 'react';
import { Dialog } from '@headlessui/react';
import { XMarkIcon, ExclamationTriangleIcon, CheckCircleIcon } from '@heroicons/react/24/outline';
import { toast } from 'react-hot-toast';
import { apiClient } from '../../services/api';
import { useServiceTypes } from '../../hooks/useServiceTypes';

interface ChangeRequest {
  id: string;
  type: 'CREATE' | 'UPDATE';
  status: string;
  requestedData: any;
  adminNotes?: string;
  companyId?: string;
}

interface ResubmitRequestModalProps {
  isOpen: boolean;
  onClose: () => void;
  request: ChangeRequest | null;
  onSuccess: () => void;
}

interface Province {
  id: string;
  nameTh: string;
  nameEn: string;
  region: string;
}

const ResubmitRequestModal: React.FC<ResubmitRequestModalProps> = ({
  isOpen,
  onClose,
  request,
  onSuccess
}) => {
  const { serviceTypes, loading: serviceTypesLoading } = useServiceTypes();
  const [provinces, setProvinces] = useState<Province[]>([]);
  const [provincesLoading, setProvincesLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const [formData, setFormData] = useState({
    nameTh: '',
    nameEn: '',
    description: '',
    phone1: '',
    phone2: '',
    phone3: '',
    email: '',
    website: '',
    lineId: '',
    facebookUrl: '',
    address: '',
    province: '',
    district: '',
    postalCode: '',
    latitude: '',
    longitude: '',
    serviceTypes: [] as string[],
    serviceAreas: [] as string[],
    companySize: '',
    establishedYear: '',
    licenseNumber: '',
    userNotes: ''
  });

  const [errors, setErrors] = useState<Record<string, string>>({});

  // Load provinces from API
  useEffect(() => {
    const fetchProvinces = async () => {
      if (!isOpen) return;

      try {
        setProvincesLoading(true);
        const response = await apiClient.get('/api/provinces/all-details');
        const provincesData = response.data.data.provinces;
        setProvinces(provincesData);
      } catch (error) {
        console.error('Failed to fetch provinces:', error);
        toast.error('ไม่สามารถโหลดข้อมูลจังหวัดได้');
      } finally {
        setProvincesLoading(false);
      }
    };

    fetchProvinces();
  }, [isOpen]);

  // Pre-fill form when modal opens
  useEffect(() => {
    if (isOpen && request) {
      const data = request.requestedData || {};

      // Convert service type names to IDs
      const serviceTypeIds = (data.serviceTypes || []).map((name: string) => {
        const found = serviceTypes.find(st => st.nameTh === name);
        return found ? found.id : name;
      }).filter(Boolean);

      // Convert province names to IDs
      const provinceIds = (data.serviceAreas || []).map((name: string) => {
        const found = provinces.find(p => p.nameTh === name);
        return found ? found.id : name;
      }).filter(Boolean);

      setFormData({
        nameTh: data.nameTh || '',
        nameEn: data.nameEn || '',
        description: data.description || '',
        phone1: data.phone1 || '',
        phone2: data.phone2 || '',
        phone3: data.phone3 || '',
        email: data.email || '',
        website: data.website || '',
        lineId: data.lineId || '',
        facebookUrl: data.facebookUrl || '',
        address: data.address || '',
        province: data.province || data.provinceId || '',
        district: data.district || '',
        postalCode: data.postalCode || '',
        latitude: data.latitude || '',
        longitude: data.longitude || '',
        serviceTypes: serviceTypeIds,
        serviceAreas: provinceIds,
        companySize: data.companySize || '',
        establishedYear: data.establishedYear?.toString() || '',
        licenseNumber: data.licenseNumber || '',
        userNotes: ''
      });
      setErrors({});
    }
  }, [isOpen, request, serviceTypes, provinces]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const handleServiceTypeToggle = (serviceTypeId: string) => {
    setFormData(prev => ({
      ...prev,
      serviceTypes: prev.serviceTypes.includes(serviceTypeId)
        ? prev.serviceTypes.filter(id => id !== serviceTypeId)
        : [...prev.serviceTypes, serviceTypeId]
    }));
  };

  const handleServiceAreaToggle = (provinceId: string) => {
    setFormData(prev => ({
      ...prev,
      serviceAreas: prev.serviceAreas.includes(provinceId)
        ? prev.serviceAreas.filter(id => id !== provinceId)
        : [...prev.serviceAreas, provinceId]
    }));
  };

  const validate = () => {
    const newErrors: Record<string, string> = {};

    if (!formData.nameTh.trim()) {
      newErrors.nameTh = 'กรุณากรอกชื่อบริษัท (ภาษาไทย)';
    }

    if (!formData.description.trim()) {
      newErrors.description = 'กรุณากรอกรายละเอียดบริษัท';
    }

    if (!formData.phone1.trim()) {
      newErrors.phone1 = 'กรุณากรอกเบอร์โทรศัพท์';
    }

    if (!formData.email.trim()) {
      newErrors.email = 'กรุณากรอกอีเมล';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'รูปแบบอีเมลไม่ถูกต้อง';
    }

    if (request?.type === 'CREATE' && !formData.province.trim()) {
      newErrors.province = 'กรุณาเลือกจังหวัด';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validate()) {
      toast.error('กรุณากรอกข้อมูลให้ครบถ้วน');
      return;
    }

    if (!request) return;

    try {
      setSubmitting(true);

      // Convert service type IDs back to names for backend
      const serviceTypeNames = formData.serviceTypes.map(id => {
        const found = serviceTypes.find(st => st.id === id);
        return found ? found.nameTh : id;
      });

      // Convert province IDs back to names for backend
      const provinceNames = formData.serviceAreas.map(id => {
        const found = provinces.find(p => p.id === id);
        return found ? found.nameTh : id;
      });

      const requestData = {
        ...formData,
        serviceTypes: serviceTypeNames,
        serviceAreas: provinceNames,
        establishedYear: formData.establishedYear ? parseInt(formData.establishedYear) : undefined,
      };

      let endpoint = '';
      let payload: any = {};

      if (request.type === 'CREATE') {
        // Resubmit as new CREATE request
        endpoint = '/api/change-requests/companies/create';
        payload = {
          requested_data: requestData,
          user_notes: formData.userNotes || `แก้ไขและส่งใหม่จากคำขอที่ถูกปฏิเสธ (ID: ${request.id})`
        };
      } else if (request.type === 'UPDATE') {
        // Resubmit as new UPDATE request
        endpoint = `/api/change-requests/companies/${request.companyId}/update`;
        payload = {
          requested_data: requestData,
          user_notes: formData.userNotes || `แก้ไขและส่งใหม่จากคำขอที่ถูกปฏิเสธ (ID: ${request.id})`
        };
      }

      await apiClient.post(endpoint, payload);
      toast.success('ส่งคำขอใหม่สำเร็จ');
      onSuccess();
      onClose();
    } catch (error: any) {
      console.error('Failed to resubmit request:', error);
      toast.error(error.response?.data?.detail || 'ไม่สามารถส่งคำขอได้');
    } finally {
      setSubmitting(false);
    }
  };

  if (!request) return null;

  return (
    <Dialog open={isOpen} onClose={onClose} className="relative z-50">
      <div className="fixed inset-0 bg-black/30" aria-hidden="true" />

      <div className="fixed inset-0 flex items-center justify-center p-4 overflow-y-auto">
        <Dialog.Panel className="mx-auto max-w-4xl w-full bg-white rounded-xl shadow-xl">
          {/* Header */}
          <div className="flex items-center justify-between p-6 border-b border-gray-200">
            <div>
              <Dialog.Title className="text-2xl font-bold text-gray-900">
                แก้ไขและส่งคำขอใหม่
              </Dialog.Title>
              <p className="mt-1 text-sm text-gray-600">
                {request.type === 'CREATE' ? 'คำขอเพิ่มบริษัทใหม่' : 'คำขอแก้ไขข้อมูลบริษัท'}
              </p>
            </div>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 transition-colors"
            >
              <XMarkIcon className="w-6 h-6" />
            </button>
          </div>

          <form onSubmit={handleSubmit} className="p-6 space-y-6">
            {/* Admin Feedback */}
            {request.adminNotes && (
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 flex items-start space-x-3">
                <ExclamationTriangleIcon className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                <div className="flex-1">
                  <h4 className="font-semibold text-amber-900 mb-1">
                    ความเห็นจากผู้ดูแลระบบ
                  </h4>
                  <p className="text-amber-800 text-sm">{request.adminNotes}</p>
                </div>
              </div>
            )}

            {/* Info Box */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 flex items-start space-x-3">
              <CheckCircleIcon className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
              <div className="flex-1 text-sm text-blue-800">
                <p className="font-medium mb-1">หมายเหตุ</p>
                <ul className="list-disc list-inside space-y-1">
                  <li>แก้ไขข้อมูลตามที่ผู้ดูแลแนะนำ</li>
                  <li>ส่งคำขอใหม่จะต้องผ่านการอนุมัติจากผู้ดูแลอีกครั้ง</li>
                  <li>คำขอเดิมจะยังคงอยู่ในสถานะ "ปฏิเสธ"</li>
                </ul>
              </div>
            </div>

            {/* Form Fields */}
            <div className="space-y-4">
              {/* Company Names */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    ชื่อบริษัท (ภาษาไทย) *
                    {request.type === 'UPDATE' && <span className="text-xs text-gray-500 ml-2">(ไม่สามารถแก้ไขได้)</span>}
                  </label>
                  <input
                    type="text"
                    name="nameTh"
                    value={formData.nameTh}
                    onChange={handleChange}
                    disabled={request.type === 'UPDATE'}
                    className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                      errors.nameTh ? 'border-red-500' : 'border-gray-300'
                    } ${request.type === 'UPDATE' ? 'bg-gray-100 cursor-not-allowed' : ''}`}
                  />
                  {errors.nameTh && <p className="mt-1 text-sm text-red-600">{errors.nameTh}</p>}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    ชื่อบริษัท (ภาษาอังกฤษ)
                  </label>
                  <input
                    type="text"
                    name="nameEn"
                    value={formData.nameEn}
                    onChange={handleChange}
                    placeholder="e.g. Security Company Co., Ltd."
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
              </div>

              {/* Description */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  รายละเอียดบริษัท *
                </label>
                <textarea
                  name="description"
                  value={formData.description}
                  onChange={handleChange}
                  rows={4}
                  className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                    errors.description ? 'border-red-500' : 'border-gray-300'
                  }`}
                />
                {errors.description && <p className="mt-1 text-sm text-red-600">{errors.description}</p>}
              </div>

              {/* Contact Info */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    เบอร์โทรศัพท์ 1 *
                  </label>
                  <input
                    type="tel"
                    name="phone1"
                    value={formData.phone1}
                    onChange={handleChange}
                    className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                      errors.phone1 ? 'border-red-500' : 'border-gray-300'
                    }`}
                  />
                  {errors.phone1 && <p className="mt-1 text-sm text-red-600">{errors.phone1}</p>}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    เบอร์โทรศัพท์ 2
                  </label>
                  <input
                    type="tel"
                    name="phone2"
                    value={formData.phone2}
                    onChange={handleChange}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    อีเมล *
                  </label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                      errors.email ? 'border-red-500' : 'border-gray-300'
                    }`}
                  />
                  {errors.email && <p className="mt-1 text-sm text-red-600">{errors.email}</p>}
                </div>
              </div>

              {/* Service Types */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  ประเภทบริการ
                </label>
                {serviceTypesLoading ? (
                  <div className="text-center py-4">กำลังโหลดประเภทบริการ...</div>
                ) : (
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-2 max-h-64 overflow-y-auto border border-gray-200 rounded-lg p-3">
                    {serviceTypes.map((serviceType) => (
                      <label key={serviceType.id} className="flex items-center space-x-2 cursor-pointer hover:bg-gray-50 p-2 rounded">
                        <input
                          type="checkbox"
                          checked={formData.serviceTypes.includes(serviceType.id)}
                          onChange={() => handleServiceTypeToggle(serviceType.id)}
                          className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                        />
                        <span className="text-sm text-gray-700">{serviceType.nameTh}</span>
                      </label>
                    ))}
                  </div>
                )}
              </div>

              {/* Service Areas */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  พื้นที่ให้บริการ
                </label>
                {provincesLoading ? (
                  <div className="flex items-center justify-center py-6 border border-gray-200 rounded-lg">
                    <div className="flex items-center space-x-2">
                      <svg className="animate-spin h-4 w-4 text-blue-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      <span className="text-sm text-gray-600">กำลังโหลดจังหวัด...</span>
                    </div>
                  </div>
                ) : (
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-2 max-h-48 overflow-y-auto border border-gray-200 rounded-lg p-3">
                    {provinces.map(province => (
                      <label key={province.id} className="flex items-center space-x-2 cursor-pointer hover:bg-gray-50 p-2 rounded">
                        <input
                          type="checkbox"
                          checked={formData.serviceAreas.includes(province.id)}
                          onChange={() => handleServiceAreaToggle(province.id)}
                          className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                        />
                        <span className="text-sm text-gray-700">{province.nameTh}</span>
                      </label>
                    ))}
                  </div>
                )}
              </div>

              {/* User Notes */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  หมายเหตุถึงผู้ดูแลระบบ
                </label>
                <textarea
                  name="userNotes"
                  value={formData.userNotes}
                  onChange={handleChange}
                  rows={3}
                  placeholder="อธิบายเหตุผลหรือการเปลี่ยนแปลงที่ทำ..."
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
            </div>

            {/* Actions */}
            <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200">
              <button
                type="button"
                onClick={onClose}
                className="px-6 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                disabled={submitting}
              >
                ยกเลิก
              </button>
              <button
                type="submit"
                disabled={submitting}
                className="px-6 py-2 text-white bg-blue-600 rounded-lg hover:bg-blue-700 transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed flex items-center space-x-2"
              >
                {submitting ? (
                  <>
                    <svg className="animate-spin h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    <span>กำลังส่ง...</span>
                  </>
                ) : (
                  <span>ส่งคำขอใหม่</span>
                )}
              </button>
            </div>
          </form>
        </Dialog.Panel>
      </div>
    </Dialog>
  );
};

export default ResubmitRequestModal;
